import React, { useCallback, useMemo, useState } from "react";
import { StackScreenProps } from "@react-navigation/stack";
import ScreenCard from "../ScreenCard";
import { useI18n } from "../../hooks/i18n-hooks";
import { useSnackbar } from "../../hooks/snackbar";
import { useMutation, useQuery } from "@apollo/client";
import { CHANGE_MISSION_SIGNER } from "../../graphql/core";
import { RootStackParamList } from "../../navigations/RootNavigation";
import { TitleMediumText } from "../../components/FormattedText";
import { GET_CLIENT_USERS } from "../../graphql/client";
import { useCurrentProfile } from "../../hooks/use-current-profile";
import VStack from "../../ui_atoms/VStack";
import ListItem from "../../ui_atoms/ListItem";
import Gap from "../../ui_atoms/Gap";
import MarkdownView from "../../components/MarkdownView";

type MissionSelectSignerScreenProps = StackScreenProps<
    RootStackParamList,
    "MissionSelectSignerScreen"
>;

const MissionSelectSignerScreen: React.FC<MissionSelectSignerScreenProps> = ({
    route,
    navigation,
}) => {
    const missionId = route.params.missionId;
    const i18n = useI18n();
    const { showSnackbar } = useSnackbar();
    const [selectedClientUserId, setSelectedClientUserId] = useState<string>();
    const [working, setWorking] = useState<boolean>(false);
    const currentProfile = useCurrentProfile();
    const [changeMissionSigner] = useMutation(CHANGE_MISSION_SIGNER);

    const { data: { getClientUsers = [] } = {} } = useQuery(GET_CLIENT_USERS, {
        variables: {
            clientId: currentProfile?.client?.id || "",
        },
        skip: !currentProfile?.client?.id,
    });
    const sendContract = useCallback(() => {
        const _func = async () => {
            if (!selectedClientUserId) {
                return;
            }
            setWorking(true);
            try {
                await changeMissionSigner({
                    variables: {
                        missionId: route.params.missionId,
                        clientUserId: selectedClientUserId,
                    },
                    errorPolicy: "all",
                });
                void showSnackbar({
                    text: i18n.t("screens.mission_select_signer.contract_sent"),
                    mode: "success",
                });
                navigation.popTo("Home", {
                    screen: "MissionsScreen",
                    params: {
                        screen: "MissionDetails",
                        params: {
                            missionId: route.params.missionId,
                        },
                    },
                });
            } catch (error) {
                void showSnackbar({
                    text: i18n.t("screens.mission_select_signer.contract_send_error"),
                    mode: "error",
                });
            } finally {
                setWorking(false);
            }
        };
        void _func();
    }, [
        changeMissionSigner,
        route.params.missionId,
        i18n,
        navigation,
        selectedClientUserId,
        showSnackbar,
    ]);

    const signLater = useCallback(() => {
        navigation.navigate("MissionDetails", {
            missionId: missionId,
        });
    }, [missionId, navigation]);

    const effectiveClientUsers = useMemo(() => {
        return getClientUsers.filter((user) => user.id !== currentProfile?.id);
    }, [currentProfile?.id, getClientUsers]);
    return (
        <ScreenCard
            buttons={[
                {
                    mode: "secondary",
                    title: i18n.t("screens.mission_select_signer.send_contract"),
                    isValid: !working && !!selectedClientUserId,
                    onPress: sendContract,
                    validating: working,
                },
                {
                    mode: "outlined",
                    title: i18n.t("screens.mission_select_signer.postpone_signing"),
                    onPress: signLater,
                    isValid: !working,
                },
            ]}
        >
            <VStack>
                <VStack h="2/3" top={1}>
                    <TitleMediumText>
                        {i18n.t("screens.mission_select_signer.description")}
                    </TitleMediumText>
                </VStack>
                <Gap gap={1} />
                <VStack>
                    {effectiveClientUsers.length > 0 ? (
                        effectiveClientUsers.map((clientUser) => (
                            <ListItem
                                useBigAvatarOrIcon
                                avatarPath={clientUser.avatarUrl}
                                key={clientUser.id}
                                title={i18n.t(`profile.title.unknown`, {
                                    firstName: clientUser.firstName,
                                    lastName: clientUser.lastName,
                                })}
                                selectable
                                checked={selectedClientUserId === clientUser.id}
                                onPress={() => {
                                    setSelectedClientUserId(clientUser.id);
                                }}
                            />
                        ))
                    ) : (
                        <VStack h="2/3" alignment="center">
                            <MarkdownView
                                markdown={i18n.t("screens.mission_select_signer.no_other_signers")}
                            />
                        </VStack>
                    )}
                </VStack>
            </VStack>
        </ScreenCard>
    );
};

export default MissionSelectSignerScreen;
